import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/team/invites/${token}`);
        setInvite(data);
      } catch (e) {
        setErr(formatError(e?.response?.data?.detail) || "Invite not found or expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    if (!user) {
      // Save token & redirect to register
      sessionStorage.setItem("pending_team_invite", token);
      nav(`/register?invite=${token}`);
      return;
    }
    setAccepting(true);
    try {
      const { data } = await api.post(`/team/invites/${token}/accept`);
      toast.success(`Welcome to ${data.team_owner}'s team!`);
      sessionStorage.removeItem("pending_team_invite");
      nav("/business/ads");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed to accept");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 flex items-center justify-center p-4" data-testid="accept-invite-page">
      <div className="max-w-md w-full border-2 border-zinc-900 dark:border-zinc-100 bg-white dark:bg-zinc-950 p-8">
        <Link to="/" className="flex items-center gap-2 font-black text-lg mb-6">
          <span className="text-brand">⚡</span> CopyForge
        </Link>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : err ? (
          <div className="text-center py-4">
            <p className="text-red-600 mb-4">{err}</p>
            <Link to="/" className="text-sm underline">Back to home</Link>
          </div>
        ) : (
          <>
            <Users className="w-10 h-10 mb-4" />
            <h1 className="text-2xl font-black mb-2">You're invited!</h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
              <span className="font-bold">{invite?.owner_name}</span> invited you to join their CopyForge Business team as a <span className="font-bold">{invite?.role}</span>.
            </p>

            <div className="border border-zinc-200 dark:border-zinc-800 p-4 mb-6 space-y-2 text-sm">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Access to the Multi-Platform Ad Generator</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Shared Brand Kit</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> All 8 social platforms unlocked</div>
            </div>

            <button
              type="button"
              onClick={accept}
              disabled={accepting}
              data-testid="accept-invite-btn"
              className="w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-6 py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {user ? "Accept invite" : "Sign up & accept"}
            </button>
            {!user && (
              <p className="text-xs text-zinc-500 text-center mt-3">
                Already have an account? <Link to={`/login?next=/team/accept/${token}`} className="underline">Login</Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
